/**
 * Notes API Client (PROJ-023)
 * Provides type-safe API calls for incident notes with multiple attachments
 * Enhanced from PROJ-007 Dynamics 365 CRM Annotation pattern
 */

import { apiRequest } from './standard-api';
import type {
  Note,
  NoteAttachment,
  CreateNotePayload,
  UpdateNotePayload,
  ListNotesParams,
  NoteDownloadResponse,
  NoteStats,
  PrepareAttachmentPayload,
  PrepareAttachmentResponse,
  CreateAttachmentPayload
} from '@/types/notes';
import type { PaginatedApiResponse } from '@/types/api';

// ============================================
// NOTES API
// ============================================

/**
 * List notes for an incident (timeline view)
 */
export async function listNotes(
  incidentId: string,
  params?: ListNotesParams
): Promise<PaginatedApiResponse<Note>> {
  const searchParams = new URLSearchParams();

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    });
  }

  const url = searchParams.toString()
    ? `/api/v1/incidents/${incidentId}/notes?${searchParams}`
    : `/api/v1/incidents/${incidentId}/notes`;

  const response = await apiRequest<Note[]>(url, { method: 'GET' });
  return {
    success: true,
    data: response.data,
    error: null,
    meta: response.meta as any
  };
}

/**
 * Get a specific note by ID
 */
export async function getNote(
  incidentId: string,
  noteId: string
): Promise<Note> {
  const response = await apiRequest<Note>(
    `/api/v1/incidents/${incidentId}/notes/${noteId}`,
    { method: 'GET' }
  );
  return response.data;
}

/**
 * Create a new note
 */
export async function createNote(
  incidentId: string,
  payload: CreateNotePayload
): Promise<Note> {
  const response = await apiRequest<Note>(
    `/api/v1/incidents/${incidentId}/notes`,
    {
      method: 'POST',
      body: JSON.stringify(payload)
    }
  );
  return response.data;
}

/**
 * Update a note (only within 15-minute window, creator only)
 */
export async function updateNote(
  incidentId: string,
  noteId: string,
  payload: UpdateNotePayload
): Promise<Note> {
  const response = await apiRequest<Note>(
    `/api/v1/incidents/${incidentId}/notes/${noteId}`,
    {
      method: 'PATCH',
      body: JSON.stringify(payload)
    }
  );
  return response.data;
}

/**
 * Delete a note (soft delete, creator only)
 */
export async function deleteNote(
  incidentId: string,
  noteId: string
): Promise<Note> {
  const response = await apiRequest<Note>(
    `/api/v1/incidents/${incidentId}/notes/${noteId}`,
    {
      method: 'DELETE'
    }
  );
  return response.data;
}

/**
 * Get note statistics for an incident
 */
export async function getNoteStats(incidentId: string): Promise<NoteStats> {
  const response = await apiRequest<NoteStats>(
    `/api/v1/incidents/${incidentId}/notes/stats`,
    { method: 'GET' }
  );
  return response.data;
}

/**
 * Download note attachment (legacy single attachment)
 */
export async function downloadNoteAttachment(
  incidentId: string,
  noteId: string
): Promise<NoteDownloadResponse> {
  const response = await apiRequest<NoteDownloadResponse>(
    `/api/v1/incidents/${incidentId}/notes/${noteId}/download`,
    { method: 'GET' }
  );
  return response.data;
}

/**
 * Generate upload URL for note attachment
 */
export async function generateUploadUrl(
  incidentId: string,
  fileName: string,
  expiresInMinutes: number = 60
): Promise<{ uploadUrl: string; blobPath: string; expiresAt: string }> {
  const response = await apiRequest<{ uploadUrl: string; blobPath: string; expiresAt: string }>(
    `/api/v1/incidents/${incidentId}/notes/upload-url`,
    {
      method: 'POST',
      body: JSON.stringify({ fileName, expiresInMinutes })
    }
  );
  return response.data;
}

// ============================================
// ATTACHMENTS API (PROJ-023)
// ============================================

/**
 * List attachments for a note
 */
export async function listAttachments(
  incidentId: string,
  noteId: string,
  includeDeleted: boolean = false
): Promise<NoteAttachment[]> {
  const query = includeDeleted ? '?includeDeleted=true' : '';
  const response = await apiRequest<NoteAttachment[]>(
    `/api/v1/incidents/${incidentId}/notes/${noteId}/attachments${query}`,
    { method: 'GET' }
  );
  return response.data;
}

/**
 * Prepare attachment upload (check for deduplication)
 */
export async function prepareAttachment(
  incidentId: string,
  noteId: string,
  payload: PrepareAttachmentPayload
): Promise<PrepareAttachmentResponse> {
  const response = await apiRequest<PrepareAttachmentResponse>(
    `/api/v1/incidents/${incidentId}/notes/${noteId}/attachments/prepare`,
    {
      method: 'POST',
      body: JSON.stringify(payload)
    }
  );
  return response.data;
}

/**
 * Create attachment record after upload
 */
export async function createAttachment(
  incidentId: string,
  noteId: string,
  payload: CreateAttachmentPayload
): Promise<NoteAttachment> {
  const response = await apiRequest<NoteAttachment>(
    `/api/v1/incidents/${incidentId}/notes/${noteId}/attachments`,
    {
      method: 'POST',
      body: JSON.stringify(payload)
    }
  );
  return response.data;
}

/**
 * Download attachment
 */
export async function downloadAttachment(
  incidentId: string,
  noteId: string,
  attachmentId: string
): Promise<NoteDownloadResponse> {
  const response = await apiRequest<NoteDownloadResponse>(
    `/api/v1/incidents/${incidentId}/notes/${noteId}/attachments/${attachmentId}/download`,
    { method: 'GET' }
  );
  return response.data;
}

/**
 * Delete attachment (soft delete)
 */
export async function deleteAttachment(
  incidentId: string,
  noteId: string,
  attachmentId: string
): Promise<void> {
  await apiRequest(
    `/api/v1/incidents/${incidentId}/notes/${noteId}/attachments/${attachmentId}`,
    { method: 'DELETE' }
  );
}

// ============================================
// UTILITIES
// ============================================

/**
 * Calculate SHA256 hash of a file
 */
export async function calculateFileHash(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number | null | undefined): string {
  if (!bytes) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Get file icon name based on mime type
 */
export function getFileIconType(mimeType?: string | null): 'image' | 'pdf' | 'spreadsheet' | 'document' | 'file' {
  if (!mimeType) return 'file';
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType === 'application/pdf') return 'pdf';
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return 'spreadsheet';
  if (mimeType.includes('document') || mimeType.includes('word')) return 'document';
  return 'file';
}

/**
 * Check if file is previewable
 */
export function isPreviewable(mimeType?: string | null): boolean {
  if (!mimeType) return false;
  return mimeType.startsWith('image/') || mimeType === 'application/pdf';
}

/**
 * Get file extension from filename
 */
export function getFileExtension(fileName: string): string {
  const parts = fileName.split('.');
  return parts.length > 1 ? parts.pop()!.toLowerCase() : '';
}

/**
 * Helper function to trigger browser download
 */
export async function triggerDownload(downloadUrl: string, fileName: string): Promise<void> {
  const a = document.createElement('a');
  a.href = downloadUrl;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

/**
 * Upload file to Azure Blob Storage
 */
export async function uploadFileToBlob(
  uploadUrl: string,
  file: File,
  onProgress?: (progress: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        const progress = Math.round((event.loaded / event.total) * 100);
        onProgress(progress);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed'));
    });

    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    xhr.send(file);
  });
}

// Legacy exports for backward compatibility
export { createNote as createTextNote };
